import { useFormContext, Controller, FieldPath, FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { Input } from '../ui/input'

function isValidPidDate(pid: string): boolean {
  const hasPlus = pid.includes('+')
  const cleaned = pid.replace(/\D/g, '')
  const len = cleaned.length
  const currentYear = new Date().getFullYear() % 100

  let year: number, month: number, day: number

  if (len === 12 || len === 8) {
    year = parseInt(cleaned.slice(0, 4), 10)
    month = parseInt(cleaned.slice(4, 6), 10)
    day = parseInt(cleaned.slice(6, 8), 10)
  } else if (len === 10 || len === 6) {
    const twoDigitYear = parseInt(cleaned.slice(0, 2), 10)
    const century = (twoDigitYear <= currentYear ? 2000 : 1900) - (hasPlus ? 100 : 0)
    year = century + twoDigitYear
    month = parseInt(cleaned.slice(2, 4), 10)
    day = parseInt(cleaned.slice(4, 6), 10)
  } else {
    return false
  }

  const birthDate = new Date(year, month - 1, day)

  return (
    birthDate.getFullYear() === year &&
    birthDate.getMonth() === month - 1 &&
    birthDate.getDate() === day &&
    // Mirrors the backend. A round-trip check alone accepts "98500312" as the year 9850,
    // and only the eight- and twelve-digit forms are exposed to it — the shorter ones
    // derive their century and can never land outside 1900-2099.
    birthDate.getTime() <= Date.now()
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const pidSchema = z
  .string()
  .refine(
    // "+" as well as "-": it marks somebody who has turned 100 and shifts the century
    // back, so a ten-digit pid cannot express their birth year without it. Rejecting it
    // did not merely block them — anyone who worked around it by typing "-" instead was
    // recorded a hundred years late, silently.
    val => /^(\d{6}|\d{8})[-+]?\d{4}$/.test(val) || /^(\d{6}|\d{8})$/.test(val),
    { message: 'defaultFormErrors.invalidFormat_pid' },
  )
  .refine(isValidPidDate, { message: 'defaultFormErrors.invalidFormat_pid' })
  // Only the hyphen is stripped. Removing the "+" would throw away the one thing that
  // distinguishes 1926 from 2026.
  .transform(val => val.replace('-', ''))

/**
 * For flows keyed on WHO somebody is, rather than on when they were born.
 *
 * An invite is stored as a hash of the whole personnummer, and that hash is the only thing
 * tying it to the person's eventual BankID login — which always reports twelve digits. A
 * pid without its last four therefore produces an account no login can ever match: the
 * invitee signs in, gets a second account, and none of the access they were invited to.
 *
 * Deliberately NOT folded into `pidSchema`. An individual is identified by their record
 * rather than by a lookup, so one can legitimately be added before a full personnummer
 * exists — a newborn, or someone waiting on a samordningsnummer.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const fullPidSchema = pidSchema.refine(val => val.length >= 10, {
  message: 'defaultFormErrors.invalidFormat_pid',
})

type PidInputProps<TFieldValues extends FieldValues = FieldValues> = {
  name: FieldPath<TFieldValues>
}

const PidInput = <TFieldValues extends FieldValues = FieldValues>({
  name,
}: PidInputProps<TFieldValues>) => {
  const { control, watch } = useFormContext<TFieldValues>()
  const { t } = useTranslation()

  const value = watch(name)

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <div>
          <Input
            autoComplete="off"
            onChange={e => field.onChange(e.target.value)}
            placeholder={t('defaultFormInputs.pid.placeholder')}
            value={value}
          />

          {error && <p className="text-sm text-error mt-1">{t(error.message, { defaultValue: error.message })}</p>}
        </div>
      )}
    />
  )
}

export default PidInput
